import { type AlarmContactDto, type SiteId } from '@solinkcorp/callhome';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { dateToDay } from '@solinkcorp/solink-components';
import { type SafeExtract } from '@solinkcorp/utils';
import { LocaleUtils } from '@solinkcorp/vd-common';
import {
  type AlarmContactEntity,
  type AlarmContactSiteAssignment,
  type AlarmProviderType,
} from 'clients/CallhomeApiTypes';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import AuthService from 'models/AuthService';
import moment from 'moment-timezone';
import { phone } from 'phone';
import { type ValueOf } from 'type-fest';
import CommonUtil from 'utils/CommonUtil';
import * as LocaleUtil from 'utils/LocaleUtil';
import {
  ALARM_CONTACT_TYPES,
  ALARM_PROVIDER_TYPES,
  MAXIMUM_CONTACT_NOONLIGHT,
  PinType,
} from 'vd-constants';
import { isNumberOnly } from './FormUtil';
import { normalizePhoneNumber } from './phoneNumberFormatUtil';
import { type VideoDiscoverySite } from './types';

const getNextAvailableIndex = (list: any[]) => {
  for (let i = 0; i < list.length; i++) {
    if (!list[i]) return i;
  }

  return -1;
};

export type UpdatableContactProperty = SafeExtract<
  AlarmContactEntity,
  'phoneNumber' | 'email' | 'name' | 'languagePreference'
>;

export const getUnassignedUsers = (masterList: any[], assigned: any[]) =>
  masterList.filter((item) => !find(assigned, ['userId', item.id]));

export const updateAlarmContactList = (
  targetIndex: number,
  item: Partial<AlarmContactEntity>,
  list: any[],
) => {
  const nextAvailableIndex = getNextAvailableIndex(list);
  let originalAssignedIndex;
  if (item.userId) {
    originalAssignedIndex = findIndex(list, ['userId', item.userId]);
  } else {
    originalAssignedIndex = findIndex(list, ['id', item.id]);
  }

  // case 1: target bin is empty, and the item is NOT already in the list
  //   just set item at the target index
  if (!list[targetIndex] && originalAssignedIndex < 0) {
    list[targetIndex] = item;
  }
  // case 2: target bin is empty, but the item is already in the list
  //   just move the item to the target index
  else if (!list[targetIndex] && originalAssignedIndex > -1) {
    list[targetIndex] = item;
    list[originalAssignedIndex] = undefined;
  }
  // case 3: target bin is not empty, but list is not full, and the item is NOT already in the list
  //   move the item at the target index to the next available index
  //   then set item at the target index
  else if (list[targetIndex] && nextAvailableIndex > -1 && originalAssignedIndex < 0) {
    list[nextAvailableIndex] = list[targetIndex];
    list[targetIndex] = item;
  }
  // case 4: target bin is not empty, and list is full, and the item is NOT already in the list
  //   just replace the item at target index
  else if (list[targetIndex] && nextAvailableIndex < 0 && originalAssignedIndex < 0) {
    list[targetIndex] = item;
  }
  // case 5: target bin is not empty, but list is not full, and the item IS already in the list
  //   swap the items
  else if (list[targetIndex] && originalAssignedIndex > -1) {
    const temp = list[targetIndex];
    list[targetIndex] = item;
    list[originalAssignedIndex] = temp;
  }
  //
  // We go through the whole updated contact list and update the priority
  // of each contact based on the index
  list.forEach((contact, index) => {
    if (contact) {
      const newPriority = index + 1;

      if (contact.priority !== newPriority) {
        list[index] = { ...contact, priority: newPriority };
      }
    }
  });
};

export const buildInitialContactList = (list: any[], assigned: any[]) => {
  assigned.forEach((item) => (list[item.priority - 1] = item));
  return list;
};

export const allowedCountries = ['ca', 'us', 'gb', 'nz'];
export const restrictedCountries = ['ca', 'us'];
export const alarmCountries = ['ca', 'us', 'gb', 'nz', 'ie', 'au'];
// Solink's Twilio-supported SMS country list. No separate entries are needed
// for Guernsey/Jersey/Isle of Man or Cocos/Christmas Island: neither phone()
// nor react-phone-input-2's own country/flag list distinguishes them from
// GB/AU respectively (a real mobile number from any of them just resolves to
// 'gb'/'au' in both).
export const smsCountries = ['ca', 'us', 'ae', 'gb', 'ie', 'au', 'nz'];

export const validatePhoneNumber = (phoneNumber: string, { strict = false } = {}) => {
  const { isValid } = phone(phoneNumber, strict ? {} : { validateMobilePrefix: false });
  return isValid;
};

// Strict variant for alarm contact flows: also rejects unassigned NANP area
// codes and non-mobile prefixes for countries that distinguish them, matching
// the carrier-routable subset CHS enforces server-side.
export const validateAlarmContactPhoneNumber = (phoneNumber: string) =>
  validatePhoneNumber(phoneNumber, { strict: true });

export type SmsPhoneNumberValidation =
  { isValid: true } | { isValid: false; reason: 'format' | 'unsupportedCountry' };

// Distinguishes *why* a phone number can't be used for SMS, so callers can
// show the right message. Uses phone()'s default (mobile-prefix-checked)
// mode, NOT the lenient `validateMobilePrefix: false` used elsewhere in this
// file: with mobile-prefix checking off, phone() reports every NANP (+1)
// number as US/CA regardless of area code, which would wrongly accept
// SMS-unsupported NANP territories like Jamaica or Barbados as if they were
// US numbers. The stricter mode also requires a mobile-shaped number, which
// is correct here since landlines can't receive SMS anyway.
export const validateSmsPhoneNumberDetailed = (phoneNumber: string): SmsPhoneNumberValidation => {
  const { isValid, countryIso2 } = phone(phoneNumber);
  if (!isValid) return { isValid: false, reason: 'format' };
  if (!countryIso2 || !smsCountries.includes(countryIso2.toLowerCase())) {
    return { isValid: false, reason: 'unsupportedCountry' };
  }
  return { isValid: true };
};

// True only if the number is both format-valid AND resolves to a
// Twilio-supported country for SMS.
export const validateSmsPhoneNumber = (phoneNumber: string) =>
  validateSmsPhoneNumberDetailed(phoneNumber).isValid;

// Picks the right validation message for an SMS phone number field, or
// `undefined` when there's nothing to show (empty field or valid number).
// Shared by EditUser and RealTimeNotifications so the two SMS-related phone
// inputs can't drift into showing different wording for the same failure.
export const getSmsPhoneNumberErrorText = (
  phoneNumber: string,
  validation: SmsPhoneNumberValidation,
  messages: { formatError: string; unsupportedCountryError: string },
): string | undefined => {
  if (!phoneNumber || validation.isValid) return undefined;
  return validation.reason === 'unsupportedCountry'
    ? messages.unsupportedCountryError
    : messages.formatError;
};

export const validateName = (name: string) => {
  return name.length > 0;
};

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Derives PIN / disarm form fields from an existing alarm contact.
 * Used by both the Add and Edit customer alarm contact panels.
 *
 * `disarmForSeconds` encoding (matches CHS / AlarmPinPanel):
 *   - null | undefined: never configured → "until next period"
 *   - -1 (or any value < 1, incl. 0): explicit "until next period" sentinel
 *   - > 0: positive duration in seconds
 * The form represents the first two as `disarmUntilNextPeriod: true`; only > 0
 * populates `disarmForMinutes`, which otherwise falls back to a 60-minute default.
 *
 * A `null` `startsOnDate`/`expiresOnDate` maps to `undefined` so the date pickers show no
 * selected day.
 */
export const deriveContactPinFields = (
  contact: Partial<
    Pick<
      AlarmContactDto,
      'accessCode' | 'disarmForSeconds' | 'pinType' | 'startsOnDate' | 'expiresOnDate'
    >
  >,
) => {
  const seconds = contact.disarmForSeconds;
  const hasPositiveDisarm = seconds != null && seconds > 0;
  return {
    accessCode: contact.accessCode ?? '',
    disarmUntilNextPeriod: !hasPositiveDisarm,
    disarmForMinutes: hasPositiveDisarm ? Math.round(seconds / 60) : 60,
    pinType: contact.pinType ?? PinType.ARM_DISARM,
    startsOnDate: contact.startsOnDate ?? undefined,
    expiresOnDate: contact.expiresOnDate ?? undefined,
  };
};

/**
 * An assignment qualifies when the contact
 * either receives alarm notifications on that site or has PIN access on it.
 */
export const sendAssignmentInPayload = (a: {
  pinEnabled: boolean;
  isNotificationsEnabled: boolean;
}): boolean => a.isNotificationsEnabled || a.pinEnabled;

/**
 * The form considers a PIN "present" when either the field has a value or it is still in the
 * masked state (server hid the existing value from us). Mirrors the save-time logic so the UI
 * gates the same way the network call does.
 */
export const isPinPresentInForm = ({
  accessCode,
  accessCodeIsMasked,
}: {
  accessCode: string;
  accessCodeIsMasked: boolean;
}): boolean => accessCodeIsMasked || !!accessCode;

/** True when any site assignment has PIN access enabled. */
export const contactHasPinEnabledOnAnySite = (
  contact: Pick<AlarmContactDto, 'siteAssignments'>,
): boolean => !!contact.siteAssignments?.some((a) => a.pinEnabled);

/** True when a stored contact has any PIN configured — either a value or an enabled assignment. */
export const contactHasConfiguredPin = (contact: AlarmContactDto): boolean =>
  !!contact.accessCode || contactHasPinEnabledOnAnySite(contact);

/**
 * True when an existing contact's PIN is being cleared by the current form values.
 * Used to gate the "remove PIN access" confirmation modal.
 */
export const isRemovingAccessCode = ({
  originalContact,
  formAccessCode,
  formAccessCodeIsMasked,
}: {
  originalContact: AlarmContactDto | undefined;
  formAccessCode: string;
  formAccessCodeIsMasked: boolean;
}): boolean => {
  if (!originalContact) return false;
  return (
    contactHasConfiguredPin(originalContact) &&
    !isPinPresentInForm({ accessCode: formAccessCode, accessCodeIsMasked: formAccessCodeIsMasked })
  );
};

/**
 * True when the form holds a different PIN value than the one already saved on the contact.
 */
export const hasPinValueChanged = ({
  originalContact,
  accessCode,
  accessCodeIsMasked,
  originalAccessCode,
}: {
  originalContact: AlarmContactDto | undefined;
  accessCode: string;
  accessCodeIsMasked: boolean;
  /** `undefined` when never fetched; `null` when the contact had no PIN. */
  originalAccessCode?: string | null;
}): boolean => {
  if (!originalContact || accessCodeIsMasked) return false;
  return originalAccessCode !== undefined
    ? (accessCode || null) !== (originalAccessCode ?? null)
    : contactHasConfiguredPin(originalContact);
};

export const PinValidityState = {
  ACTIVE: 'ACTIVE',
  NOT_STARTED: 'NOT_STARTED',
  EXPIRED: 'EXPIRED',
} as const;
export type PinValidityState = ValueOf<typeof PinValidityState>;

/** The timezone PIN dates are checked in. A missing or unknown timezone uses UTC, which matches the server. */
export const resolvePinTimeZone = (timeZone: string | null | undefined): string =>
  timeZone && moment.tz.zone(timeZone) ? timeZone : 'UTC';

/**
 * Classifies a PIN's validity on one site. The PIN is active from 00:00 on `startsOnDate` through
 * the end of `expiresOnDate`, in the site's timezone.
 */
export function getSitePinValidityState(
  contact: Pick<AlarmContactDto, 'startsOnDate' | 'expiresOnDate'>,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): PinValidityState {
  const todayDay = dateToDay(now, resolvePinTimeZone(timeZone));
  // ISO `YYYY-MM-DD` strings sort chronologically, so plain string comparison is correct.
  if (contact.expiresOnDate && contact.expiresOnDate < todayDay) return PinValidityState.EXPIRED;
  if (contact.startsOnDate && contact.startsOnDate > todayDay) return PinValidityState.NOT_STARTED;
  return PinValidityState.ACTIVE;
}

/**
 * Counts the PIN-enabled sites where the PIN is expired or not started yet. `allUserSitesById`
 * supplies the timezones of sites outside `locationsById`. Sites the user cannot access are
 * counted as hidden.
 */
export function getPinValiditySummary(
  contact: Pick<AlarmContactDto, 'startsOnDate' | 'expiresOnDate' | 'siteAssignments'>,
  allUserSitesById: Partial<Record<SiteId, Pick<VideoDiscoverySite, 'timezone'>>> | undefined,
  locationsById: Partial<Record<SiteId, Pick<VideoDiscoverySite, 'timezone'>>>,
  now: Date = new Date(),
) {
  const pinSites = (contact.siteAssignments ?? [])
    .filter((a) => a.pinEnabled)
    .map((a) => allUserSitesById?.[a.siteId] ?? locationsById[a.siteId]);
  const states = pinSites.map((site) => getSitePinValidityState(contact, site?.timezone, now));
  return {
    totalCount: states.length,
    hiddenCount: pinSites.filter((site) => !site).length,
    expiredCount: states.filter((state) => state === PinValidityState.EXPIRED).length,
    notStartedCount: states.filter((state) => state === PinValidityState.NOT_STARTED).length,
  };
}

export const isRemoteGuardPin = (pinType: PinType | null | undefined): boolean =>
  pinType === PinType.REMOTE_GUARD;

export const getPinTypeLabel = (pinType: PinType | null | undefined, locale: LocaleDefinition) =>
  isRemoteGuardPin(pinType) ? locale.alarms.remoteGuard : locale.alarms.standardPin;

export function formatPinDisarmTime(
  disarmForSeconds: number | undefined,
  locale: LocaleDefinition,
): string {
  return !disarmForSeconds
    ? ''
    : disarmForSeconds === -1
      ? locale.alarms.dtmfPinUserDisarmUntilNextPeriod
      : `${disarmForSeconds / 60} minute(s)`;
}

type PinAccessContact = AlarmContactDto | AlarmContactEntity;

/**
 * True when the contact has PIN access on the given site.
 *
 * Handles both shapes:
 * - new-style `siteAssignments[]` with a per-site `pinEnabled` flag
 * - legacy single `siteId` support (e.g., DTMFPIN contacts)
 *
 * Phase 3 cleanup: when legacy DTMFPIN contacts are migrated to `siteAssignments[]`, delete the fallback
 */
export function hasPinEnabledOnSite(contact: PinAccessContact, siteId: SiteId): boolean {
  const { siteAssignments } = contact;
  if (siteAssignments?.length) {
    return siteAssignments.some((sa) => sa.siteId === siteId && sa.pinEnabled);
  }
  return contact.siteId != null && contact.siteId === siteId;
}

function contactOverlapsSites(contact: PinAccessContact, siteIds: Set<SiteId>): boolean {
  const { siteAssignments } = contact;
  if (siteAssignments?.length) {
    return siteAssignments.some((sa) => siteIds.has(sa.siteId));
  }
  return contact.siteId != null && siteIds.has(contact.siteId);
}

/** True if the contact uses this access code on any scoped site with PIN enabled */
function hasPinEnabledOnScopedSites(
  contact: PinAccessContact,
  scopedSiteIds: Set<SiteId>,
): boolean {
  for (const siteId of scopedSiteIds) {
    if (hasPinEnabledOnSite(contact, siteId)) return true;
  }
  return false;
}

/**
 * Validates the given access code.  Returns an error message if the access code is invalid.
 *
 * When `selectedSiteIds` is set, the code must be unique among contacts that have the same code
 * **and** have PIN enabled on at least one overlapping site — so a contact with `pinEnabled:
 * false` on a site may reuse another contact's code on that site.
 */
export function validatePinAccessCode(
  locale: LocaleDefinition,
  allContacts: AlarmContactDto[] | AlarmContactEntity[],
  contactId: string | undefined,
  accessCode: string,
  accessCodeLength: number,
  selectedSiteIds?: Set<SiteId>,
): string | undefined {
  if (accessCode.length !== accessCodeLength) {
    return LocaleUtil.getLocalizedStr({
      string: locale.alarms.dtmfPinUserPinInvalidLength,
      replacements: { length: accessCodeLength },
    });
  }

  if (!isNumberOnly(accessCode)) {
    return locale.alarms.dtmfPinUserPinInvalidCharacter;
  }

  const contacts = allContacts as PinAccessContact[];
  const conflicting = contacts.find((c) => {
    if (c.id === contactId || c.accessCode !== accessCode) {
      return false;
    }
    if (selectedSiteIds != null && !contactOverlapsSites(c, selectedSiteIds)) {
      return false;
    }
    return selectedSiteIds == null || hasPinEnabledOnScopedSites(c, selectedSiteIds);
  });

  if (conflicting) {
    return locale.alarms.dtmfPinUserPinInUse;
  }
}

export function validateContactPanelDisarmPeriod(
  disarmForMinutes: number,
  minDisarmMins: number,
  maxDisarmMins: number,
  locale: LocaleDefinition,
) {
  if (!disarmForMinutes || disarmForMinutes > maxDisarmMins || disarmForMinutes < minDisarmMins) {
    return LocaleUtils.getLocalizedStr(locale.alarms.disarmPeriodInvalid, {
      min: minDisarmMins,
      max: maxDisarmMins,
    });
  }
}

/** STANDARD / EXTERNAL participate in per-site call-order priority; DTMFPIN and GUEST do not. */
export const isAlarmContactInSiteCallPriorityList = (
  contact: Pick<AlarmContactDto, 'type'>,
): boolean =>
  contact.type === ALARM_CONTACT_TYPES.STANDARD || contact.type === ALARM_CONTACT_TYPES.EXTERNAL;

/**
 * Returns true if the contact participates in the call order for the given site.
 * Requires the contact to be STANDARD/EXTERNAL AND have `isNotificationsEnabled !== false`
 * for that specific site assignment. Contacts with `isNotificationsEnabled = false` are
 * PIN-only on that site (priority -1) and are excluded from the call order.
 */
export function isContactInCallOrderForSite(contact: AlarmContactDto, siteId: SiteId): boolean {
  if (!isAlarmContactInSiteCallPriorityList(contact)) return false;
  const assignment = contact.siteAssignments?.find((a) => a.siteId === siteId);
  if (!assignment) return false;
  return assignment.isNotificationsEnabled !== false;
}

/**
 * True when `contact` has the lowest call-order priority number on `siteId` among all
 * notification-enabled contacts.
 *
 * Note: priority is not guaranteed to start at 1 (e.g. after a prior primary was deleted
 * without server-side rebalancing), so this compares against peers rather than checking
 * `priority === 1`.
 */
export function isContactPrimaryOnSite(
  contact: AlarmContactDto,
  siteId: SiteId,
  allContacts: AlarmContactDto[],
): boolean {
  const myAssignment = contact.siteAssignments?.find((a) => a.siteId === siteId);
  if (!myAssignment) return false;
  const myPriority = myAssignment.priority;
  return !allContacts.some(
    (c) =>
      c.id !== contact.id &&
      isContactInCallOrderForSite(c, siteId) &&
      (c.siteAssignments?.find((a) => a.siteId === siteId)?.priority ?? Infinity) < myPriority,
  );
}

/**
 * Highest `siteAssignments.priority` on `siteId` among contacts in the call order for that site
 * (STANDARD/EXTERNAL with `isNotificationsEnabled !== false`), optionally excluding one contact.
 * Used for non-primary slots so priority stays after the current max.
 */
export function getMaxCallOrderPriorityAmongPeersOnSite(
  siteId: SiteId,
  allContacts: AlarmContactDto[],
  excludeContactId?: string,
): number {
  let maxP = 0;
  for (const c of allContacts) {
    if (excludeContactId && c.id === excludeContactId) continue;
    if (!isContactInCallOrderForSite(c, siteId)) continue;
    for (const sa of c.siteAssignments ?? []) {
      if (sa.siteId === siteId) maxP = Math.max(maxP, sa.priority);
    }
  }
  return maxP;
}

/**
 * Number of call-order contacts (STANDARD/EXTERNAL with `isNotificationsEnabled !== false`)
 * Used when computing the end-of-list slot for a contact demoted from primary on that site.
 */
export function getCallOrderPeerCountOnSite(
  siteId: SiteId,
  allContacts: AlarmContactDto[],
  excludeContactId?: string,
): number {
  let count = 0;
  for (const c of allContacts) {
    if (excludeContactId && c.id === excludeContactId) continue;
    if (isContactInCallOrderForSite(c, siteId)) count += 1;
  }
  return count;
}

export function getCallOrderPriorityForContactOnSite(
  contact: Pick<AlarmContactDto, 'siteAssignments'>,
  siteId: SiteId,
): number | undefined {
  return contact.siteAssignments?.find((a) => a.siteId === siteId)?.priority;
}

/**
 * After moving `movingContactId` to 1-based slot `newPriority1Based` among `callOrderContacts`
 * (STANDARD/EXTERNAL already assigned to `siteId`), returns each contact id → full `siteAssignments`
 * with updated priorities on `siteId` (1..n contiguous).
 */
export function buildSiteAssignmentUpdatesAfterCallOrderPriority(
  siteId: SiteId,
  callOrderContacts: AlarmContactDto[],
  movingContactId: string,
  newPriority1Based: number,
): Map<string, AlarmContactSiteAssignment[]> {
  const updates = new Map<string, AlarmContactSiteAssignment[]>();
  const sorted = [...callOrderContacts]
    .filter((c) => isContactInCallOrderForSite(c, siteId))
    .sort((a, b) => {
      const pa = getCallOrderPriorityForContactOnSite(a, siteId) ?? 0;
      const pb = getCallOrderPriorityForContactOnSite(b, siteId) ?? 0;
      return pa - pb;
    });
  const n = sorted.length;
  if (n === 0) return updates;
  const fromIdx = sorted.findIndex((c) => c.id === movingContactId);
  if (fromIdx < 0) return updates;
  const toIdx = Math.max(0, Math.min(newPriority1Based - 1, n - 1));
  if (fromIdx === toIdx) return updates;
  const [moved] = sorted.splice(fromIdx, 1);
  sorted.splice(toIdx, 0, moved);
  for (let i = 0; i < sorted.length; i++) {
    const c = sorted[i];
    const newP = i + 1;
    const list = c.siteAssignments;
    if (!list?.length) continue;
    const siteAssignments = list.map((sa) =>
      sa.siteId === siteId ? { ...sa, priority: newP } : sa,
    );
    updates.set(c.id, siteAssignments);
  }
  return updates;
}

export type CallOrderPriorityUpdate = {
  alarmContactId: string;
  siteAssignments: NonNullable<AlarmContactDto['siteAssignments']>;
};

/**
 * Builds bulk siteAssignment updates from a drag-ordered call list.
 * Priority is 1-based index; contacts whose priority on `siteId` is already correct are omitted.
 */
export function buildCallOrderPriorityUpdates(
  orderedContacts: AlarmContactDto[],
  siteId: SiteId,
): CallOrderPriorityUpdate[] {
  return orderedContacts
    .map((contact, index) => {
      const newPriority = index + 1;
      const currentPriority = getCallOrderPriorityForContactOnSite(contact, siteId);
      if (currentPriority === newPriority) return null;
      return {
        alarmContactId: contact.id,
        siteAssignments: (contact.siteAssignments ?? []).map((sa) =>
          sa.siteId === siteId ? { ...sa, priority: newPriority } : sa,
        ),
      };
    })
    .filter((u): u is NonNullable<typeof u> => u !== null);
}

/**
 * Sites where the contact was primary (priority 1) on the previous record but is no longer
 * primary (or no longer in the call order at all). Used to trigger a shift-up of the remaining peers
 */
export function getSitesWhereContactLeavesPrimary(
  newSiteAssignments: AlarmContactSiteAssignment[],
  previousContact: AlarmContactDto | undefined,
  isEditMode: boolean,
): SiteId[] {
  if (!isEditMode || !previousContact) return [];
  const sites: SiteId[] = [];
  for (const prev of previousContact.siteAssignments ?? []) {
    if (prev.priority !== 1) continue;
    if (prev.isNotificationsEnabled === false) continue;
    const next = newSiteAssignments.find((a) => a.siteId === prev.siteId);
    const stillPrimary = !!next && next.priority === 1 && next.isNotificationsEnabled !== false;
    if (!stillPrimary) sites.push(prev.siteId);
  }
  return [...new Set(sites)];
}

/**
 * When a contact leaves the primary slot on a set of sites, reassigns the remaining
 * STANDARD/EXTERNAL peers to dense sequential priorities starting at 1, sorted by their
 * current priority. The demoted contact takes the last slot via its own assignment payload.
 */
export function buildShiftedDownPrioritiesForOtherContacts(
  allContacts: AlarmContactDto[],
  siteIds: Set<SiteId>,
  excludeContactId?: string,
): BumpSitePrioritiesForContactsPayload[] {
  const affectedContacts = allContacts.filter((c) => {
    if (excludeContactId && c.id === excludeContactId) return false;
    if (!isAlarmContactInSiteCallPriorityList(c)) return false;
    if (!c.siteAssignments?.length) return false;
    return c.siteAssignments.some(
      (a) => siteIds.has(a.siteId) && a.isNotificationsEnabled !== false,
    );
  });

  const updatedAssignments = new Map<string, AlarmContactSiteAssignment[]>(
    affectedContacts.map((c) => [c.id, [...(c.siteAssignments ?? [])]]),
  );

  for (const siteId of siteIds) {
    const contactsOnSite = affectedContacts
      .map((c) => {
        const a = c.siteAssignments?.find((sa) => sa.siteId === siteId);
        if (!a || a.isNotificationsEnabled === false) return null;
        return { id: c.id, priority: a.priority };
      })
      .filter((x) => x !== null)
      .sort((a, b) => a!.priority - b!.priority);

    contactsOnSite.forEach((entry, index) => {
      const id = entry!.id;
      const newPriority = index + 1;
      const assignments = updatedAssignments.get(id)!;
      updatedAssignments.set(
        id,
        assignments.map((a) => (a.siteId === siteId ? { ...a, priority: newPriority } : a)),
      );
    });
  }

  return affectedContacts.reduce<BumpSitePrioritiesForContactsPayload[]>((acc, c) => {
    const updated = updatedAssignments.get(c.id)!;
    const changed = (c.siteAssignments ?? []).some(
      (orig) => orig.priority !== updated.find((u) => u.siteId === orig.siteId)?.priority,
    );
    if (changed) acc.push({ alarmContactId: c.id, siteAssignments: updated });
    return acc;
  }, []);
}

/**
 * Combines multiple priority-rebalance payloads into a single payload per contact, overlaying
 * each group's changes against the original siteAssignments. Groups MUST target disjoint sites
 * per contact (e.g. a "becomes primary" set and a "leaves primary" set never share a site).
 */
export function mergePriorityRebalancePayloads(
  allContacts: AlarmContactDto[],
  ...payloadGroups: BumpSitePrioritiesForContactsPayload[][]
): BumpSitePrioritiesForContactsPayload[] {
  const originalsById = new Map<string, AlarmContactSiteAssignment[]>(
    allContacts.map((c) => [c.id, c.siteAssignments ?? []]),
  );
  const affectedIds = new Set<string>();
  for (const group of payloadGroups) {
    for (const p of group) affectedIds.add(p.alarmContactId);
  }

  const out: BumpSitePrioritiesForContactsPayload[] = [];
  for (const id of affectedIds) {
    const originals = originalsById.get(id) ?? [];
    const priorityBySite = new Map<SiteId, number>(originals.map((sa) => [sa.siteId, sa.priority]));

    for (const group of payloadGroups) {
      const payload = group.find((p) => p.alarmContactId === id);
      if (!payload) continue;
      for (const sa of payload.siteAssignments) {
        const orig = originals.find((o) => o.siteId === sa.siteId)?.priority;
        if (orig !== sa.priority) priorityBySite.set(sa.siteId, sa.priority);
      }
    }

    const updated = originals.map((sa) => ({ ...sa, priority: priorityBySite.get(sa.siteId)! }));
    const changed = originals.some((o, i) => o.priority !== updated[i].priority);
    if (changed) out.push({ alarmContactId: id, siteAssignments: updated });
  }
  return out;
}

/**
 * Sites where the saved payload assigns priority 1 and this contact was not already primary on that site.
 * Used to know when to bump other contacts' priorities before persisting the save.
 */
export function getSitesWhereContactBecomesPrimary(
  newSiteAssignments: AlarmContactSiteAssignment[],
  previousContact: AlarmContactDto | undefined,
  isEditMode: boolean,
): SiteId[] {
  const sites: SiteId[] = [];
  for (const sa of newSiteAssignments) {
    if (sa.priority !== 1) continue;
    if (!isEditMode || !previousContact) {
      sites.push(sa.siteId);
      continue;
    }
    const prev = previousContact.siteAssignments?.find((a) => a.siteId === sa.siteId);
    if (prev?.priority !== 1) {
      sites.push(sa.siteId);
    }
  }
  return [...new Set(sites)];
}

export type BumpSitePrioritiesForContactsPayload = {
  alarmContactId: string;
  siteAssignments: AlarmContactSiteAssignment[];
};

/**
 * When a contact is promoted to primary (priority 1) on a set of sites, reassigns all other
 * STANDARD/EXTERNAL contacts on those sites to dense sequential priorities starting at 2,
 * sorted by their current priority. This prevents gaps (e.g. a contact at priority 6 staying
 * at 7 after a new primary is inserted when only 6 contacts exist).
 */
export function buildBumpedPrioritiesForOtherContacts(
  allContacts: AlarmContactDto[],
  siteIds: Set<SiteId>,
  excludeContactId?: string,
): BumpSitePrioritiesForContactsPayload[] {
  const affectedContacts = allContacts.filter((c) => {
    if (excludeContactId && c.id === excludeContactId) return false;
    if (!isAlarmContactInSiteCallPriorityList(c)) return false;
    if (!c.siteAssignments?.length) return false;
    return c.siteAssignments.some(
      (a) => siteIds.has(a.siteId) && a.isNotificationsEnabled !== false,
    );
  });

  // Work on mutable copies of each contact's site assignments
  const updatedAssignments = new Map<string, AlarmContactSiteAssignment[]>(
    affectedContacts.map((c) => [c.id, [...(c.siteAssignments ?? [])]]),
  );

  // For each affected site, sort contacts by their current priority and assign 2, 3, 4, ...
  for (const siteId of siteIds) {
    const contactsOnSite = affectedContacts
      .map((c) => {
        const a = c.siteAssignments?.find((sa) => sa.siteId === siteId);
        if (!a || a.isNotificationsEnabled === false) return null;
        return a ? { id: c.id, priority: a.priority } : null;
      })
      .filter((x) => x !== null)
      .sort((a, b) => a!.priority - b!.priority);

    contactsOnSite.forEach((entry, index) => {
      const id = entry!.id;
      const newPriority = index + 2; // 1 is reserved for the promoted contact
      const assignments = updatedAssignments.get(id)!;
      updatedAssignments.set(
        id,
        assignments.map((a) => (a.siteId === siteId ? { ...a, priority: newPriority } : a)),
      );
    });
  }

  // Only return contacts whose assignments actually changed
  return affectedContacts.reduce<BumpSitePrioritiesForContactsPayload[]>((acc, c) => {
    const updated = updatedAssignments.get(c.id)!;
    const changed = (c.siteAssignments ?? []).some(
      (orig) => orig.priority !== updated.find((u) => u.siteId === orig.siteId)?.priority,
    );
    if (changed) acc.push({ alarmContactId: c.id, siteAssignments: updated });
    return acc;
  }, []);
}

export type AlarmContactSiteDiff = {
  /** Sites the contact was newly assigned to (not in previous assignments). */
  added: SiteId[];
  /** Sites the contact was already on and remains on after the save. */
  updated: SiteId[];
  /** Sites the contact was removed from. */
  removed: SiteId[];
};

/**
 * Computes which sites were added, updated, or removed between two sets of site assignments.
 * Pass `undefined` for `previousAssignments` when creating a brand-new contact — all sites
 * will be returned in `added`.
 */
export function computeAlarmContactSiteDiff(
  previousAssignments: AlarmContactSiteAssignment[] | undefined,
  newAssignments: AlarmContactSiteAssignment[],
): AlarmContactSiteDiff {
  const prevSiteIds = new Set(previousAssignments?.map((a) => a.siteId) ?? []);
  const newSiteIds = new Set(newAssignments.map((a) => a.siteId));

  return {
    added: newAssignments.filter((a) => !prevSiteIds.has(a.siteId)).map((a) => a.siteId),
    updated: newAssignments.filter((a) => prevSiteIds.has(a.siteId)).map((a) => a.siteId),
    removed: (previousAssignments ?? [])
      .filter((a) => !newSiteIds.has(a.siteId))
      .map((a) => a.siteId),
  };
}

/** The contact-level fields that, when changed, cause an update event on every assigned site. */
type ContactBodySnapshot = {
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  type?: string;
  disarmForSeconds?: number | null;
  pinType?: PinType | null;
  startsOnDate?: string | null;
  expiresOnDate?: string | null;
};

/**
 * Blank email matches a missing email. An omitted PIN type matches Standard.
 * A missing `startsOnDate` or `expiresOnDate` matches `null`.
 * Disarm `-1` is until the next period. Any other disarm value is whole minutes.
 *
 * `identityChanged` (name, email, phone, type) audits every site the contact stays on.
 * `pinChanged` (type, disarm time, start, expiry) audits only sites with `pinEnabled`.
 */
function contactAuditChanges(
  savedContact: ContactBodySnapshot,
  previousContact: ContactBodySnapshot | undefined,
): { identityChanged: boolean; pinChanged: boolean } {
  if (!previousContact) return { identityChanged: false, pinChanged: false };

  const disarmForTime = (seconds: number | null | undefined) =>
    seconds == null ? null : seconds === -1 ? -1 : Math.round(seconds / 60);
  const pinDay = (day: string | null | undefined) => day || null;

  return {
    identityChanged:
      (savedContact.name ?? null) !== (previousContact.name ?? null) ||
      (savedContact.email?.trim() || null) !== (previousContact.email?.trim() || null) ||
      normalizePhoneNumber(savedContact.phoneNumber) !==
        normalizePhoneNumber(previousContact.phoneNumber) ||
      (savedContact.type ?? null) !== (previousContact.type ?? null),
    // the accessCode change is handled by pinValueChanged since VD has no accessCode visibility
    pinChanged:
      disarmForTime(savedContact.disarmForSeconds) !==
        disarmForTime(previousContact.disarmForSeconds) ||
      (savedContact.pinType ?? PinType.ARM_DISARM) !==
        (previousContact.pinType ?? PinType.ARM_DISARM) ||
      pinDay(savedContact.startsOnDate) !== pinDay(previousContact.startsOnDate) ||
      pinDay(savedContact.expiresOnDate) !== pinDay(previousContact.expiresOnDate),
  };
}

/**
 * From a list of sites that exist in both old and new assignments (`updated` from
 * `computeAlarmContactSiteDiff`), returns only those that should have an
 * `alarmContactUpdate` event fired.
 *
 * A site qualifies if:
 * - Name, email, phone, or contact type changed — affects ALL sites, OR
 * - PIN data changed (pin type, disarm time, startsOnDate, expiresOnDate, or the PIN value) — affects only
 *   sites whose saved assignment has pinEnabled, OR
 * - The site-specific data changed (priority, pinEnabled, isNotificationsEnabled) — affects only THAT site.
 *
 * `pinValueChanged` is passed in by the caller because the list payload no longer carries
 * `accessCode`, so comparing it between snapshots here is not reliable.
 */
export function filterContactUpdateSites(
  updatedSiteIds: SiteId[],
  previousAssignments: AlarmContactSiteAssignment[] | undefined,
  newAssignments: AlarmContactSiteAssignment[],
  savedContact: ContactBodySnapshot,
  previousContact: ContactBodySnapshot | undefined,
  pinValueChanged: boolean = false,
): SiteId[] {
  const { identityChanged, pinChanged } = contactAuditChanges(savedContact, previousContact);

  return updatedSiteIds.filter((siteId) => {
    if (identityChanged) return true;
    const prev = previousAssignments?.find((a) => a.siteId === siteId);
    const next = newAssignments.find((a) => a.siteId === siteId);
    const assignmentChanged =
      prev?.priority !== next?.priority ||
      prev?.pinEnabled !== next?.pinEnabled ||
      (prev?.isNotificationsEnabled ?? true) !== (next?.isNotificationsEnabled ?? true);
    const pinDataChanged =
      !!next?.pinEnabled && (pinChanged || (pinValueChanged && !!prev?.pinEnabled));
    return assignmentChanged || pinDataChanged;
  });
}

type AuditSiteAssignment = Pick<
  AlarmContactSiteAssignment,
  'pinEnabled' | 'isNotificationsEnabled'
>;

function buildAssignmentTypeLine(assignment: AuditSiteAssignment): string | undefined {
  const isContact = !!assignment.isNotificationsEnabled;
  const isPin = !!assignment.pinEnabled;
  if (isContact && isPin) return 'Type: Contact / PIN';
  if (isContact) return 'Type: Contact';
  if (isPin) return 'Type: PIN';
  return undefined;
}

/**
 * Builds the `notes` audit-log field for an `alarmContact{Add,Update,Remove}` event — a
 * "- "-bulleted list of plain-English lines describing the remaining assignment type plus
 * any action that just happened, led by a newline so the "Notes:" label in the audit log
 * renders on its own line above the bullets. Returns `undefined` when nothing applies.
 *
 * The action (add/update/remove) is implicit in which of `prev`/`next` is passed: omit `prev`
 * for an add (no prior assignment), omit `next` for a remove (no assignment afterward).
 *
 * - "Type: Contact / PIN" | "Type: Contact" | "Type: PIN" is one line for the CURRENT
 *   (post-save) assignment. Omitted when the site was fully unassigned (`next` missing).
 * - "Contact Added" / "PIN Added" mark the moment that capability just turned on.
 * - "Contact Removed" / "PIN Disabled" mark the moment it just turned off.
 * - "PIN Removed" replaces "PIN Disabled" when the transition to off was caused by deleting
 *   the PIN from the contact entirely (`pinFullyRemoved`), rather than a per-site toggle.
 */
export function buildAlarmContactAuditNotes({
  prev,
  next,
  pinValueChanged = false,
  pinDisarmChanged = false,
  pinFullyRemoved = false,
}: {
  prev?: AuditSiteAssignment;
  next?: AuditSiteAssignment;
  pinValueChanged?: boolean;
  pinDisarmChanged?: boolean;
  pinFullyRemoved?: boolean;
}): string | undefined {
  const lines: string[] = [];

  if (next) {
    const typeLine = buildAssignmentTypeLine(next);
    if (typeLine) lines.push(typeLine);
  }

  if (next?.isNotificationsEnabled) {
    if (!prev?.isNotificationsEnabled) lines.push('Contact Added');
  } else if (prev?.isNotificationsEnabled) {
    lines.push('Contact Removed');
  }

  if (next?.pinEnabled) {
    if (!prev?.pinEnabled) lines.push('PIN Added');
    if (pinValueChanged) lines.push('PIN Updated');
    if (pinDisarmChanged) lines.push('PIN Disarm Time Updated');
  } else if (prev?.pinEnabled) {
    lines.push(pinFullyRemoved ? 'PIN Removed' : 'PIN Disabled');
  }

  // Leading newline pushes the "Notes:" label in the audit log onto its own line, with every
  // item below it as a "- " bulleted line.
  return lines.length > 0 ? `\n${lines.map((line) => `- ${line}`).join('\n')}` : undefined;
}

export const isContactExternal = (contact: Partial<AlarmContactEntity>) =>
  contact.type === ALARM_CONTACT_TYPES.EXTERNAL;
export const isContactStandard = (contact: Partial<AlarmContactEntity>) =>
  contact.type === ALARM_CONTACT_TYPES.STANDARD;

/**
 * Returns names of sites (from `locationsById`) where `contact` is the only assigned
 * STANDARD or EXTERNAL contact. Only STANDARD and EXTERNAL contacts participate in the
 * per-site call-priority list; DTMFPIN and GUEST contacts are excluded from this check.
 *
 * Used to prevent deleting or fully unassigning a contact that is the sole alarm contact
 * on one or more sites.
 */
export function getSitesWhereContactIsLast(
  contact: AlarmContactDto,
  allContacts: AlarmContactDto[],
  locationsById: { [id: SiteId]: { name: string } },
): string[] {
  if (
    contact.type !== ALARM_CONTACT_TYPES.STANDARD &&
    contact.type !== ALARM_CONTACT_TYPES.EXTERNAL
  ) {
    return [];
  }

  const siteNames: string[] = [];
  for (const assignment of contact.siteAssignments ?? []) {
    const { siteId } = assignment;
    if (!locationsById[siteId]) continue; // only visible sites
    // Only block on sites where this contact is an alarm contact (isNotificationsEnabled=true).
    // PIN-only assignments (isNotificationsEnabled=false) don't count toward the last-contact guard.
    if (assignment.isNotificationsEnabled === false) continue;
    const othersOnSite = allContacts.some(
      (c) => c.id !== contact.id && isContactInCallOrderForSite(c, siteId),
    );
    if (!othersOnSite) {
      siteNames.push(locationsById[siteId].name);
    }
  }
  return siteNames;
}

/** Minimum shape a contact needs for per-site grouping. */
export type SiteAssignableContact = Pick<AlarmContactDto, 'type' | 'siteAssignments'>;

export interface SiteContacts<T extends SiteAssignableContact = AlarmContactDto> {
  /** STANDARD and EXTERNAL contacts assigned to this site, sorted by site priority. */
  siteAlarmContacts: T[];
  /** GUEST contacts assigned to this site, sorted by site priority. */
  siteGuestContacts: T[];
  /**
   * Non-GUEST contacts with active PIN access on this site — i.e., the contact has an
   * `accessCode` AND the site assignment has `pinEnabled: true`. Includes STANDARD/EXTERNAL
   * contacts when their PIN is enabled here (they will also appear in `siteAlarmContacts`
   * when notifications are enabled), as well as DTMFPIN-style pin-only contacts.
   *
   * Name kept for backwards-compatibility; "pin-only" is historical — semantically this is
   * "all contacts with PIN active on this site".
   */
  sitePinContacts: T[];
}

const EMPTY_SITE_CONTACTS: SiteContacts<SiteAssignableContact> = {
  siteAlarmContacts: [],
  siteGuestContacts: [],
  sitePinContacts: [],
};

// Callers (filterDefinitions.ts, isLocationAlarmConfigured, AlarmConfigurations)
// each resolve contacts per-location, and several run over the same location
// list in the same render — cache the per-site grouping by contacts-array
// identity so a given `customerAlarmContacts` array is only scanned once
// instead of once per (predicate, location) pair.
const siteContactsCache = new WeakMap<
  SiteAssignableContact[],
  Map<SiteId, SiteContacts<SiteAssignableContact>>
>();

function groupContactsBySite<T extends SiteAssignableContact>(
  contacts: T[],
): Map<SiteId, SiteContacts<T>> {
  const bySite = new Map<
    SiteId,
    {
      alarmContactsWithPriority: Array<{ contact: T; priority: number }>;
      siteGuestContacts: T[];
      sitePinContacts: T[];
    }
  >();

  for (const c of contacts) {
    // Mirrors the original `siteAssignments?.find((a) => a.siteId === siteId)`
    // lookup: only the first assignment for a given site counts.
    const seenSiteIds = new Set<SiteId>();
    for (const assignment of c.siteAssignments ?? []) {
      if (seenSiteIds.has(assignment.siteId)) continue;
      seenSiteIds.add(assignment.siteId);

      let entry = bySite.get(assignment.siteId);
      if (!entry) {
        entry = { alarmContactsWithPriority: [], siteGuestContacts: [], sitePinContacts: [] };
        bySite.set(assignment.siteId, entry);
      }

      if (c.type === 'GUEST') {
        entry.siteGuestContacts.push(c);
      } else {
        const isAlarmNotified = assignment.isNotificationsEnabled !== false;
        if (isAlarmNotified) {
          entry.alarmContactsWithPriority.push({ contact: c, priority: assignment.priority });
        }
        if (assignment.pinEnabled) {
          entry.sitePinContacts.push(c);
        }
      }
    }
  }

  const result = new Map<SiteId, SiteContacts<T>>();
  for (const [siteId, entry] of bySite) {
    const siteAlarmContacts = entry.alarmContactsWithPriority
      .sort((a, b) => a.priority - b.priority)
      .map(({ contact }) => contact);
    result.set(siteId, {
      siteAlarmContacts,
      siteGuestContacts: entry.siteGuestContacts,
      sitePinContacts: entry.sitePinContacts,
    });
  }
  return result;
}

export function getContactsForSite<T extends SiteAssignableContact = AlarmContactDto>(
  contacts: T[] | undefined,
  siteId: SiteId,
): SiteContacts<T> {
  if (!contacts) return EMPTY_SITE_CONTACTS as SiteContacts<T>;

  let bySite = siteContactsCache.get(contacts) as Map<SiteId, SiteContacts<T>> | undefined;
  if (!bySite) {
    bySite = groupContactsBySite(contacts);
    siteContactsCache.set(contacts, bySite as Map<SiteId, SiteContacts<SiteAssignableContact>>);
  }
  return bySite.get(siteId) ?? (EMPTY_SITE_CONTACTS as SiteContacts<T>);
}

/**
 * Returns the display label for an alarm contact type.
 * DTMFPIN contacts show as "PIN Only"; all others (STANDARD, EXTERNAL) show as "Alarm Contact".
 */
export function getContactTypeLabel(
  isNotificationsEnabled: boolean,
  locale: { alarms: { contactTypePinOnly: string; contactTypeAlarmContact: string } },
): string {
  return isNotificationsEnabled
    ? locale.alarms.contactTypeAlarmContact
    : locale.alarms.contactTypePinOnly;
}

export const getMaxContactCount = (providerType: string) => {
  const MAXIMUM_CONTACT_SOLINK = get(
    AuthService,
    'session.clientSettings.maxSelfMonitorContacts',
    5,
  );

  /**
   * The expression after the OR operator allows self serve to add 5 alarm contacts
   * there are no plans atm the address this as it would require CH script to remove
   * all the extra contacts for self-serve customers
   */
  return providerType === ALARM_PROVIDER_TYPES.NOONLIGHT ||
    providerType === ALARM_PROVIDER_TYPES.MONITOR_CENTER
    ? MAXIMUM_CONTACT_NOONLIGHT
    : MAXIMUM_CONTACT_SOLINK;
};

/**
 * Get array of available priorities for assignment when adding new contacts
 */
export const getAvailablePriorities = (
  contacts: Partial<AlarmContactEntity>[],
  maxContactCount: number,
): number[] => {
  // array of all possible priorities from 1 to maxContactCount
  const possiblePriorities = Array.from({ length: maxContactCount }, (_, i) => i + 1);

  const availablePriorities = possiblePriorities.filter((priority) => {
    return !contacts.some((contact) => contact.priority === priority);
  });

  // contact list is full
  if (availablePriorities.length === 0) {
    return [];
  }

  return availablePriorities;
};
interface AllContactsAreValidParams {
  contacts: Partial<AlarmContactEntity>[];
  providerType: AlarmProviderType;
}

export const allContactsAreValid = ({ contacts, providerType }: AllContactsAreValidParams) =>
  contacts.every((contact) => alarmContactIsValid({ contact, providerType }));

interface ContactIsValidParams {
  contact?: Partial<AlarmContactEntity>;
  providerType?: AlarmProviderType;
}

export const alarmContactIsValid = ({ contact, providerType }: ContactIsValidParams) => {
  if (!contact || !providerType) return false;
  let contactIsValid = true;
  const { name, phoneNumber, email } = contact;

  /* Noonlight requires a phoneNumber for alarm contacts
     Self-service mode requires either a phoneNumber or Email
     Name is required for both types of service providers
  */

  if (
    providerType === ALARM_PROVIDER_TYPES.NOONLIGHT ||
    providerType === ALARM_PROVIDER_TYPES.MONITOR_CENTER
  ) {
    // If email is provided, must be valid
    const invalidEmail = email && !CommonUtil.isEmailValid(email);

    // Must have valid name and phoneNumber
    const invalidNameOrPhoneNumber =
      !phoneNumber || !name || !validateAlarmContactPhoneNumber(phoneNumber) || !validateName(name);

    if (invalidEmail || invalidNameOrPhoneNumber) {
      contactIsValid = false;
    }
  } else {
    // Must always have a name
    const invalidName = !name || !validateName(name);

    // Both email and phoneNumber must be valid, if they've both been entered
    const invalidEmailOrPhoneNumberWithBoth =
      email &&
      phoneNumber &&
      (!CommonUtil.isEmailValid(email) || !validateAlarmContactPhoneNumber(phoneNumber));

    // Email must be valid if no number is provided
    const invalidEmailWithNoNumber = !phoneNumber && email && !CommonUtil.isEmailValid(email);

    // PhoneNumber must be valid if no email is provided
    const invalidNumberWithNoEmail =
      !email && phoneNumber && !validateAlarmContactPhoneNumber(phoneNumber);

    // No email and phoneNumber
    const invalidEmailAndNumber = !email && !phoneNumber;

    if (
      invalidName ||
      invalidEmailWithNoNumber ||
      invalidNumberWithNoEmail ||
      invalidEmailOrPhoneNumberWithBoth ||
      invalidEmailAndNumber
    ) {
      contactIsValid = false;
    }
  }
  return contactIsValid;
};

/** A contact or Solink user that already uses a given email / phone number. */
export interface AlarmContactMethodOwner {
  /** Display name of the contact / user already using this email or phone. */
  name: string;
  /** Whether the match came from an existing alarm contact or a Solink user. */
  source: 'contact' | 'user';
}

/** Minimal shape of a Solink user needed for duplicate-method lookups. */
interface AlarmContactLookupUser {
  id: string;
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

interface FindAlarmContactMethodOwnerParams {
  /** The raw value typed into the form (email or phone). */
  value: string;
  existingContacts: AlarmContactDto[];
  users: AlarmContactLookupUser[];
  /** Contact currently loaded in the form — excluded so it never flags itself. */
  excludeContactId?: string | null;
  /** Solink user currently linked to the form — excluded so it never flags itself. */
  excludeUserId?: string | null;
}

const normalizeEmailForCompare = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase();

/**
 * Finds an existing contact or Solink user (other than the one currently loaded) whose email
 * matches `value`. Returns `undefined` when there is no conflict.
 */
export const findAlarmContactWithEmail = ({
  value,
  existingContacts,
  users,
  excludeContactId,
  excludeUserId,
}: FindAlarmContactMethodOwnerParams): AlarmContactMethodOwner | undefined => {
  const target = normalizeEmailForCompare(value);
  if (!target) return undefined;
  const contact = existingContacts.find(
    (c) => c.id !== excludeContactId && normalizeEmailForCompare(c.email) === target,
  );
  if (contact) return { name: contact.name ?? '', source: 'contact' };
  const user = users.find(
    (u) => u.id !== excludeUserId && normalizeEmailForCompare(u.email) === target,
  );
  if (user) return { name: user.name ?? '', source: 'user' };
  return undefined;
};

/**
 * Finds an existing contact or Solink user (other than the one currently loaded) whose phone
 * number matches `value`. Returns `undefined` when there is no conflict. Callers should only invoke
 * this once `value` is a valid phone number.
 */
export const findAlarmContactWithPhone = ({
  value,
  existingContacts,
  users,
  excludeContactId,
  excludeUserId,
}: FindAlarmContactMethodOwnerParams): AlarmContactMethodOwner | undefined => {
  const target = normalizePhoneNumber(value);
  if (!target) return undefined;
  const contact = existingContacts.find(
    (c) => c.id !== excludeContactId && normalizePhoneNumber(c.phoneNumber) === target,
  );
  if (contact) return { name: contact.name ?? '', source: 'contact' };
  const user = users.find(
    (u) => u.id !== excludeUserId && normalizePhoneNumber(u.phoneNumber) === target,
  );
  if (user) return { name: user.name ?? '', source: 'user' };
  return undefined;
};

/**
 * True when at least one assignment has `isNotificationsEnabled=true` on a non solink provider site.
 * All provides other than SOLINK requires a phone number to dispatch, so the panel must gate save when this is true
 * and the contact has no phone.
 */
export function hasMonitoredSite(
  siteAssignments: { siteId: SiteId; isNotificationsEnabled: boolean }[],
  locationsById: { [id: SiteId]: { alarmConfig?: { providerType?: AlarmProviderType } } },
): boolean {
  return siteAssignments.some((a) => {
    if (!a.isNotificationsEnabled) return false;
    const providerType = locationsById[a.siteId]?.alarmConfig?.providerType;
    return providerType && providerType !== ALARM_PROVIDER_TYPES.SOLINK;
  });
}

/**
 * Generates a new PIN 4 digit for a given contact
 * Temporary, will be replaced with a PIN generator in CHS
 * so that we can avoid duplicate PINs on save
 */
export function generatePin() {
  return `${Math.floor(Math.random() * 10000)}`.padStart(4, '0');
}
